import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Container } from "reactstrap";

import DemoNavbar from "components/Navbars/DemoNavbar.js";
import MainFooter from "components/Footers/MainFooter.js";
import StudioPlanCheckout, { profileGroup, sellerLine } from "components/Studio/StudioPlanCheckout";
import { LegalLinks } from "components/Legal/LegalConsent";
import { countryGroup } from "config/legal";
import { useUserAuth } from "context/UserContext";
import { studioApi } from "services/studioService";
import { contractLang, fixedT, localeFor, planFeatures, priceLine, siteLang, tierLabel } from "components/Studio/studioText";
import "../../styles/brand-auth.css";
import "./studio.css";

/* /studio/plans: the public Studio plans page. Outside RestrictedRoute: a
   visitor can read the offer before having an account.

   The offer comes from the backend (GET /codenec/studio/plans): prices per
   country, which countries are on sale, the seller. Nothing about plans is
   hard-coded here beyond the wording. When plans are not on sale the page
   says "Coming soon" and offers no checkout.

   "Show prices for" only filters what the cards show. It starts from the
   profile's country, then ?country=, then the last choice in this browser;
   with nothing chosen, every card lists every country's price. The
   checkout's own country question is separate and asked again. */

const STORE_KEY = "codenec.studioPlans.country";
const readStored = () => {
  try {
    return window.localStorage.getItem(STORE_KEY);
  } catch {
    return null;
  }
};
const store = (code) => {
  try {
    window.localStorage.setItem(STORE_KEY, code);
  } catch {
    // private mode: the choice just isn't remembered
  }
};

const ENDED = new Set(["canceled", "incomplete_expired"]);
const PAID_TIERS = new Set(["starter", "business"]);

// What the signed-in user has: "plan" (a Studio plan not ended), "comp",
// "trial", or null.
function holding(mine) {
  const plan = mine?.plan;
  const billing = mine?.billing;
  if (plan?.source === "comp" && plan.status === "active") return "comp";
  if (plan?.source === "stripe" && ["active", "pending", "suspended"].includes(plan.status)) return "plan";
  if (billing && billing.status && !ENDED.has(billing.status)) return "plan";
  if (plan?.source === "trial" && plan.status === "trial") return "trial";
  return null;
}

function StudioPlans() {
  const { t, i18n } = useTranslation();
  const { user } = useUserAuth();
  const [searchParams] = useSearchParams();
  const lang = siteLang(i18n);

  const [offer, setOffer] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [mine, setMine] = useState(null);
  const [shown, setShown] = useState(null);
  const [checkoutTier, setCheckoutTier] = useState(null);
  const chooseRefs = useRef({});
  // Set once the visitor picks a country themselves; until then the default
  // follows the profile (which may arrive after the offer).
  const picked = useRef(false);

  const signedIn = Boolean(user && !user.isAnonymous);
  const invited = user?.studioEnabled === true;

  const loadOffer = useCallback(() => {
    setLoadError(null);
    return studioApi
      .getPlans()
      .then(setOffer)
      .catch(() => setLoadError(true));
  }, []);

  useEffect(() => {
    loadOffer();
  }, [loadOffer]);

  const userRef = useRef(user);
  userRef.current = user;
  useEffect(() => {
    if (!signedIn) return;
    studioApi
      .plan(userRef.current)
      .then((data) => setMine(data || {}))
      .catch(() => setMine({ failed: true }));
  }, [signedIn]);

  const groups = (offer?.countries || []).filter((c) => c.sellable).map((c) => c.code);
  const onSale = Boolean(offer?.onSale);

  // The country shown by default, once the offer says what is on sale.
  useEffect(() => {
    if (!offer || picked.current) return;
    const sellable = (offer.countries || []).filter((c) => c.sellable).map((c) => c.code);
    const param = countryGroup(searchParams.get("country"));
    const stored = readStored();
    setShown(
      profileGroup(user, sellable) ||
        (sellable.includes(param) ? param : null) ||
        (sellable.includes(stored) ? stored : null)
    );
  }, [offer, searchParams, user]);

  const pickShown = (code) => {
    picked.current = true;
    setShown(code);
    store(code);
  };

  const closeCheckout = () => {
    const tier = checkoutTier;
    setCheckoutTier(null);
    window.requestAnimationFrame(() => chooseRefs.current[tier]?.focus());
  };

  const has = holding(mine);
  const mxRow = (offer?.countries || []).find((c) => c.code === "MX");
  const crRow = (offer?.countries || []).find((c) => c.code === "CR");

  // One price sentence for a country; Mexico and Costa Rica read theirs in
  // Spanish, like their contract.
  const priceFor = (plan, code) => {
    const price = plan.prices?.[code];
    if (!price) return null;
    const pLang = contractLang(lang, code);
    const line = priceLine(fixedT(i18n, t, pLang), price, localeFor(pLang, code));
    return (
      <p key={code} className="studio-plan-card__price">
        {!shown && <span>{t(`studio.plans.countries.${code}`)}: </span>}
        <span lang={pLang !== lang ? pLang : undefined}>{line}</span>
      </p>
    );
  };

  const cta = (plan) => {
    const paid = PAID_TIERS.has(plan.tier);
    if (paid && !onSale) return <p className="studio-chip">{t("studio.plans.comingSoon")}</p>;
    if (!signedIn) {
      return (
        <div className="studio-plan-card__cta">
          <Link className="studio-btn studio-btn-primary studio-btn-small" to="/register-page">{t("studio.plans.cta.signUp")}</Link>
          <Link className="studio-btn studio-btn-small" to="/login-page">{t("studio.plans.cta.signIn")}</Link>
        </div>
      );
    }
    if (!invited) {
      return (
        <div className="studio-plan-card__cta">
          <p className="studio-muted">{t("studio.plans.cta.inviteOnly")}</p>
          <Link className="studio-btn studio-btn-small" to="/contact">{t("studio.plans.cta.askInvite")}</Link>
        </div>
      );
    }
    if (!paid) {
      return has === "trial"
        ? <p className="studio-chip studio-chip-done">{t("studio.plans.cta.yourTrial")}</p>
        : <p className="studio-muted">{t("studio.plans.cta.trialInviteOnly")}</p>;
    }
    if (!mine) return null;
    if (has === "comp") return <p className="studio-muted">{t("studio.plans.cta.comp")}</p>;
    if (has === "plan") {
      return mine.plan?.tier === plan.tier ? (
        <div className="studio-plan-card__cta">
          <p className="studio-chip studio-chip-done">{t("studio.plans.cta.yourPlan")}</p>
          <Link to="/accountsettings?tab=billing">{t("studio.plans.cta.manage")}</Link>
        </div>
      ) : (
        <div className="studio-plan-card__cta">
          <Link to="/accountsettings?tab=billing">{t("studio.plans.cta.change")}</Link>
        </div>
      );
    }
    return (
      <div className="studio-plan-card__cta">
        <button
          type="button"
          className="studio-btn studio-btn-primary studio-btn-small"
          ref={(el) => { chooseRefs.current[plan.tier] = el; }}
          aria-expanded={checkoutTier === plan.tier}
          aria-controls={checkoutTier === plan.tier ? "studio-plan-checkout" : undefined}
          onClick={() => setCheckoutTier(plan.tier)}
        >
          {t("studio.plans.cta.choose", { plan: tierLabel(t, plan.tier) })}
        </button>
      </div>
    );
  };

  const fair = offer?.fairUse || {};
  const refund = offer?.refund || {};
  const numberLocale = localeFor(lang);

  return (
    <>
      <DemoNavbar />
      <main className="studio studio-home studio-plans">
        <section className="studio-home__band cn-grid">
          <span className="cn-grain" aria-hidden="true" />
          <Container style={{ position: "relative", zIndex: 2 }}>
            <div className="studio-eyebrow">{t("studio.plans.eyebrow")}</div>
            <h1>{t("studio.plans.title")}</h1>
            <p>{t("studio.plans.lead")}</p>
          </Container>
        </section>

        <Container className="studio-home__shell">
          <div className="studio-panel">
            {searchParams.get("checkout") === "cancel" && (
              <div className="studio-notice" role="status"><p>{t("studio.plans.canceled")}</p></div>
            )}

            {loadError ? (
              <div className="studio-notice" role="alert">
                <p>{t("studio.plans.loadError")}</p>
                <button type="button" className="studio-btn studio-btn-small" onClick={loadOffer}>{t("studio.plans.retry")}</button>
              </div>
            ) : !offer ? (
              <p className="studio-muted studio-loading">{t("studio.common.loading")}</p>
            ) : (
              <>
                {!onSale && (
                  <div className="studio-notice">
                    <p><strong>{t("studio.plans.comingSoon")}</strong> {t("studio.plans.comingSoonBody")}</p>
                  </div>
                )}

                {onSale && groups.length > 0 && (
                  <fieldset className="studio-fieldset studio-plans-country">
                    <legend>{t("studio.plans.showPricesFor")}</legend>
                    {groups.map((code) => (
                      <label key={code} className="studio-radio">
                        <input
                          type="radio"
                          name="studio-plans-country"
                          value={code}
                          checked={shown === code}
                          onChange={() => pickShown(code)}
                        />
                        <span>{t(`studio.plans.countries.${code}`)}</span>
                      </label>
                    ))}
                  </fieldset>
                )}
                {onSale && mxRow && !mxRow.sellable && <p className="studio-muted">{t("studio.plans.mxNotYet")}</p>}
                {onSale && crRow && !crRow.sellable && <p className="studio-muted">{t("studio.plans.crNotYet")}</p>}

                <ul className="studio-plan-cards">
                  {(offer.plans || []).map((plan) => (
                    <li key={plan.tier} className="studio-plan-card">
                      <h2 id={`studio-plan-${plan.tier}`}>{tierLabel(t, plan.tier)}</h2>
                      {plan.tier === "free" ? (
                        <p className="studio-plan-card__price">{t("studio.plans.trialPrice")}</p>
                      ) : onSale ? (
                        <>
                          {(shown ? [shown] : groups).map((code) => priceFor(plan, code))}
                          <p className="studio-muted">{t("studio.plans.fairUse")}</p>
                        </>
                      ) : null}
                      <ul className="studio-plan-card__features">
                        {planFeatures(t, plan).map((line) => <li key={line}>{line}</li>)}
                      </ul>
                      {cta(plan)}
                    </li>
                  ))}
                </ul>

                {checkoutTier && onSale && (
                  <StudioPlanCheckout
                    key={checkoutTier}
                    tier={checkoutTier}
                    offer={offer}
                    onClose={closeCheckout}
                    onPricesChanged={loadOffer}
                  />
                )}

                <section className="studio-plans-info" aria-labelledby="studio-plans-message">
                  <h2 id="studio-plans-message">{t("studio.plans.message.title")}</h2>
                  <p>{t("studio.plans.message.body")}</p>
                  <table className="studio-plans-table">
                    <caption>{t("studio.plans.message.caption")}</caption>
                    <thead>
                      <tr>
                        <th scope="col">{t("studio.plans.message.request")}</th>
                        <th scope="col">{t("studio.plans.message.uses")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {["ex1", "ex2", "ex3", "ex4"].map((ex) => (
                        <tr key={ex}>
                          <td>{t(`studio.plans.message.${ex}`)}</td>
                          <td>{t(`studio.plans.message.${ex}Uses`)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </section>

                <section className="studio-plans-info" aria-labelledby="studio-plans-not-counted">
                  <h2 id="studio-plans-not-counted">{t("studio.plans.notCounted.title")}</h2>
                  <ul>
                    <li>{t("studio.plans.notCounted.nothingSaved")}</li>
                    <li>{t("studio.plans.notCounted.ourError")}</li>
                    <li>{t("studio.plans.notCounted.deadline")}</li>
                    <li>{t("studio.plans.notCounted.fix")}</li>
                  </ul>
                </section>

                <section className="studio-plans-info" aria-labelledby="studio-plans-fair-use">
                  <h2 id="studio-plans-fair-use">{t("studio.plans.fairUseSection.title")}</h2>
                  <ul>
                    <li>{t("studio.plans.fairUseSection.capacity", { from: fair.capacityMeterFrom ?? 75 })}</li>
                    <li>
                      {t("studio.plans.fairUseSection.traffic", {
                        gb: fair.gbPerApp ?? 20,
                        reads: new Intl.NumberFormat(numberLocale).format(fair.readsPerApp ?? 3000000),
                      })}
                    </li>
                    <li>{t("studio.plans.fairUseSection.reset")}</li>
                  </ul>
                </section>

                <section className="studio-plans-info" aria-labelledby="studio-plans-billing">
                  <h2 id="studio-plans-billing">{t("studio.plans.billing.title")}</h2>
                  <ul>
                    <li>{t("studio.plans.billing.monthly")}</li>
                    <li>{t("studio.plans.billing.upgrade")}</li>
                    <li>{t("studio.plans.billing.downgrade")}</li>
                    <li>{t("studio.plans.billing.cancel")}</li>
                    <li>{t("studio.plans.billing.lapse")}</li>
                    <li>{t("studio.plans.billing.priceChange")}</li>
                  </ul>
                </section>

                <section className="studio-plans-info" aria-labelledby="studio-plans-refund">
                  <h2 id="studio-plans-refund">{t("studio.plans.refund.title")}</h2>
                  <p>
                    {t("studio.plans.refund.body", {
                      days: refund.firstPaymentDays ?? 10,
                      max: refund.maxMessages ?? 20,
                      renewalDays: refund.accidentalRenewalDays ?? 3,
                    })}
                  </p>
                </section>

                <section className="studio-plans-info" aria-labelledby="studio-plans-where">
                  <h2 id="studio-plans-where">{t("studio.plans.where.title")}</h2>
                  <p>{t("studio.plans.where.body")}</p>
                </section>

                <section className="studio-plans-info" aria-labelledby="studio-plans-seller">
                  <h2 id="studio-plans-seller">{t("studio.plans.seller.title")}</h2>
                  <p>{sellerLine(t, offer.seller)}</p>
                  <LegalLinks newTab={false} />
                </section>
              </>
            )}
          </div>
        </Container>
      </main>
      <MainFooter />
    </>
  );
}

export default StudioPlans;
